import { logo } from '../assets/images';
import { Briefcase, Award, FileText, Zap } from 'lucide-react';

interface LoadingTransitionProps {
  message?: string;
  icon?: 'sparkles' | 'file';
  jobInfo?: {
    position?: string;
    level?: string;
    type?: string;
  };
  isPressureInterview?: boolean;
}

export default function LoadingTransition({
  message = 'AI面试官正在准备，请稍后',
  jobInfo,
  isPressureInterview = false,
}: LoadingTransitionProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B0F19] via-[#0F172A] to-[#1E293B] relative overflow-hidden flex items-center justify-center">
      {/* 背景装饰 - 与首页保持一致 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-[120px] animate-pulse" />
        <div
          className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[100px] animate-pulse"
          style={{ animationDelay: '2s' }}
        />
        <div className="absolute top-[40%] left-[50%] transform -translate-x-1/2 w-[800px] h-[800px] bg-amber-600/5 rounded-full blur-[150px]" />
      </div>

      {/* 网格背景 */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(245, 158, 11, 0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(245, 158, 11, 0.5) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      />

      {/* 主要内容 */}
      <div className="relative z-10 text-center">
        {/* AI 图标动画 */}
        <div className="relative mb-8">
          {/* 外圈光环 */}
          <div className="absolute inset-0 w-32 h-32 mx-auto">
            <div
              className="absolute inset-0 rounded-full border-2 border-amber-500/20 animate-ping"
              style={{ animationDuration: '2s' }}
            />
            <div
              className="absolute inset-2 rounded-full border-2 border-yellow-500/20 animate-ping"
              style={{ animationDuration: '2s', animationDelay: '0.5s' }}
            />
          </div>

          {/* 中心图标 */}
          <div className="relative w-32 h-32 mx-auto flex items-center justify-center">
            <img src={logo} alt="面面帮" className="w-28 h-28 object-contain" />
          </div>
        </div>

        {/* 主标题 */}
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 animate-fade-in">
          {message}
        </h2>

        {/* 加载动画条 */}
        <div className="w-64 h-1 bg-slate-700 rounded-full mx-auto overflow-hidden">
          <div className="h-full bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 rounded-full animate-loading-bar" />
        </div>

        {/* 加载提示点 */}
        <div className="flex items-center justify-center gap-2 mt-6">
          <span
            className="w-2 h-2 bg-amber-500 rounded-full animate-bounce"
            style={{ animationDelay: '0s' }}
          />
          <span
            className="w-2 h-2 bg-amber-500 rounded-full animate-bounce"
            style={{ animationDelay: '0.2s' }}
          />
          <span
            className="w-2 h-2 bg-amber-500 rounded-full animate-bounce"
            style={{ animationDelay: '0.4s' }}
          />
        </div>

        {/* 面试信息卡片 */}
        {jobInfo && (jobInfo.position || jobInfo.level || jobInfo.type) && (
          <div className="mt-8 w-[350px] mx-auto bg-slate-800/60 border border-amber-500/20 rounded-2xl p-4 backdrop-blur-sm animate-fade-in">
            <div className="flex flex-wrap items-center justify-center gap-4">
              {jobInfo.position && (
                <div className="flex items-center gap-2 text-slate-300">
                  <Briefcase size={16} className="text-amber-400" />
                  <span className="text-sm">{jobInfo.position}</span>
                </div>
              )}
              {jobInfo.level && (
                <div className="flex items-center gap-2 text-slate-300">
                  <Award size={16} className="text-amber-400" />
                  <span className="text-sm">{jobInfo.level}</span>
                </div>
              )}
              {jobInfo.type && (
                <div className="flex items-center gap-2 text-slate-300">
                  <FileText size={16} className="text-emerald-400" />
                  <span className="text-sm">{jobInfo.type}</span>
                </div>
              )}
            </div>
            {/* 压力面试标识 */}
            {isPressureInterview && (
              <div className="mt-3 pt-3 border-t border-red-500/20 flex items-center justify-center gap-2">
                <Zap size={16} className="text-red-400" />
                <span className="text-sm font-medium text-red-400">
                  压力面试模式
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CSS 动画 */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes loading-bar {
          0% {
            transform: translateX(-100%);
          }
          50% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(100%);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.6s ease-out forwards;
          opacity: 0;
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        .animate-loading-bar {
          animation: loading-bar 2s ease-in-out infinite;
          width: 50%;
        }
      `}</style>
    </div>
  );
}
